import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useJobActions } from "@react/domains/jobs";

import type { GithubRepo, Project } from "@apis/memory/models";

import { useAuth } from "../../../shared/contexts/auth/useAuth.ts";
import { useQueries } from "../../../shared/hooks/useQueries.ts";

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useMemoryActions() {
  const { api } = useAuth();

  const queryClient = useQueryClient();

  const {
    getMemoriesQueryOptions,
    getMemoryProjectQueryOptions,
    getMemoryProjectsQueryOptions,
  } = useQueries();

  const { updateJob } = useJobActions();

  const invalidateMemories = async (
    projectId: Project["id"],
  ): Promise<void> => {
    const memoriesQueryOptions = getMemoriesQueryOptions({ projectId });

    await queryClient.invalidateQueries({
      queryKey: memoriesQueryOptions.groupQueryKey,
    });
  };

  const invalidateProject = async (id: Project["id"]): Promise<void> => {
    const projectQueryOptions = getMemoryProjectQueryOptions({ id });

    await queryClient.invalidateQueries({
      queryKey: projectQueryOptions.groupQueryKey,
    });
  };

  const invalidateProjects = async (): Promise<void> => {
    const projectsQueryOptions = getMemoryProjectsQueryOptions();

    await queryClient.invalidateQueries({
      queryKey: projectsQueryOptions.groupQueryKey,
    });
  };

  const addGithubRepoToProject = useMutation({
    mutationFn: api.memory.addGithubRepoToProject,
    onSuccess: async (_, { projectId }) => {
      await invalidateProject(projectId);
      await invalidateProjects();
    },
  });

  const archiveMemory = useMutation({
    mutationFn: api.memory.archiveMemory,
    onSuccess: async (_, { projectId }) => {
      await invalidateMemories(projectId);
    },
  });

  const createProject = useMutation({
    mutationFn: api.memory.createProject,
    onSuccess: invalidateProjects,
  });

  const generateMemoryApiKey = useMutation({
    mutationFn: api.memory.generateMemoryApiKey,
    onSuccess: async (_, { projectId }) => {
      await invalidateProject(projectId);
      await invalidateProjects();
    },
  });

  const reindexGithubComments = useMutation({
    mutationFn: async ({
      githubRepoId,
    }: {
      readonly githubRepoId: GithubRepo["id"];
      readonly projectId: Project["id"];
    }) => api.memory.reindexGithubComments({ githubRepoId }),
    onSuccess: async ({ job }, { projectId }) => {
      updateJob(job);
      await invalidateProject(projectId);
    },
  });

  const removeGithubRepoFromProject = useMutation({
    mutationFn: api.memory.removeGithubRepoFromProject,
    onSuccess: async (_, { projectId }) => {
      await invalidateProject(projectId);
      await invalidateProjects();
    },
  });

  const unarchiveMemory = useMutation({
    mutationFn: api.memory.unarchiveMemory,
    onSuccess: async (_, { projectId }) => {
      await invalidateMemories(projectId);
    },
  });

  return {
    addGithubRepoToProject,
    archiveMemory,
    createProject,
    generateMemoryApiKey,
    reindexGithubComments,
    removeGithubRepoFromProject,
    unarchiveMemory,
  };
}
